import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { _T, C, CSS } from '../shared/theme';
import { s_PB, s_GB } from '../shared/styles';
import { MOOD_OPTIONS, MOOD_TAGS_NL, MOOD_TAGS_EN } from '../shared/constants';
import { syncSleepEntry, syncMoodEntry } from '../shared/dbService';

/**
 * SleepMoodCheckinModal
 *
 * Props:
 *   lang        – "nl" | "en"
 *   date        – YYYY-MM-DD string (defaults to today)
 *   onClose()   – called when dismissed without saving
 *   onSaved(payload) – called after saving; payload: { date, hours, quality, sleepScore, mood, tags }
 */
export default function SleepMoodCheckinModal({ lang, date, onClose, onSaved }) {
  const nl = lang === 'nl';
  const todayKey = new Date().toISOString().slice(0, 10);
  const targetDate = date || todayKey;

  // Load existing data for this date so the user can edit it
  const existingSleep = (() => {
    try { return JSON.parse(localStorage.getItem('sl_sleep_log') || '[]').find(e => e.date === targetDate) || null; } catch { return null; }
  })();
  const existingMood = (() => {
    try { return JSON.parse(localStorage.getItem('sl_mood_log') || '[]').find(e => e.date === targetDate) || null; } catch { return null; }
  })();

  const [step,    setStep]    = useState(0);
  const [hours,   setHours]   = useState(existingSleep?.hours ?? existingSleep?.durationMins != null ? Math.round((existingSleep?.durationMins || 420) / 60) : 7);
  const [quality, setQuality] = useState(existingSleep?.quality ?? 3);
  const [selMood, setSelMood] = useState(existingMood?.mood ?? null);
  const [tags,    setTags]    = useState(existingMood?.tags ?? []);
  const [note,    setNote]    = useState(existingMood?.note ?? '');

  const sleepScore = Math.min(10, Math.round((Math.min(hours, 8) / 8) * 7 + quality * 0.6));

  // Human-readable date label
  const dateLabel = (() => {
    if (targetDate === todayKey) return nl ? 'Vandaag' : 'Today';
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    if (targetDate === yesterday.toISOString().slice(0, 10)) return nl ? 'Gisteren' : 'Yesterday';
    const d = new Date(targetDate);
    return d.toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'short' });
  })();

  const isEditing = !!(existingSleep || existingMood);

  const save = () => {
    const ts = Date.now();
    // Sleep
    const sleepEntry = { date: targetDate, hours, durationMins: Math.round(hours * 60), quality, score: sleepScore, ts };
    try {
      const log  = JSON.parse(localStorage.getItem('sl_sleep_log') || '[]');
      const next = [sleepEntry, ...log.filter(e => e.date !== targetDate)].sort((a, b) => b.date.localeCompare(a.date));
      localStorage.setItem('sl_sleep_log', JSON.stringify(next));
    } catch {}
    syncSleepEntry(sleepEntry);
    // Mood
    if (selMood) {
      const moodOpt  = MOOD_OPTIONS.find(m => m.id === selMood);
      const moodEntry = { date: targetDate, mood: selMood, score: moodOpt?.score ?? 3, tags, note, ts };
      try {
        const log  = JSON.parse(localStorage.getItem('sl_mood_log') || '[]');
        const next = [moodEntry, ...log.filter(e => e.date !== targetDate)].sort((a, b) => b.date.localeCompare(a.date));
        localStorage.setItem('sl_mood_log', JSON.stringify(next));
      } catch {}
      syncMoodEntry(moodEntry);
    }
    onSaved?.({ date: targetDate, hours, quality, sleepScore, mood: selMood, tags });
    onClose();
  };

  const QUICK_TAGS = (nl ? MOOD_TAGS_NL : MOOD_TAGS_EN).slice(0, 8);

  return ReactDOM.createPortal(
    <div
      style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(5,8,18,0.88)', backdropFilter:'blur(8px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width:'100%', maxWidth:480, background:_T.bg2, borderRadius:'24px 24px 0 0', border:`1px solid ${_T.cardBorderTop}`, padding:'24px 24px 32px', maxHeight:'88vh', overflowY:'auto', position:'relative' }}>
        <button onClick={onClose} style={{position:'absolute',top:14,right:14,background:'transparent',border:'none',color:C.silver,fontSize:20,cursor:'pointer',padding:4,lineHeight:1,zIndex:2}}>✕</button>
        <style>{CSS}</style>

        {/* Drag handle */}
        <div style={{ width:36, height:4, borderRadius:2, background:_T.cardBorderBot, margin:'0 auto 20px' }}/>

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
          <div>
            <div style={{ fontSize:9, color:_T.accent, letterSpacing:3, fontWeight:600, marginBottom:4 }}>
              {isEditing ? (nl?'BEWERKEN':'EDIT') : (nl?'DAGELIJKSE CHECK-IN':'DAILY CHECK-IN')}
            </div>
            <div style={{ fontFamily:'Georgia,serif', fontSize:20, color:C.white, lineHeight:1.2 }}>
              {step===0 ? (nl?'Hoe heb je geslapen? 😴':'How did you sleep? 😴') : (nl?'Hoe voel je je? 💭':'How are you feeling? 💭')}
            </div>
            <div style={{ fontSize:12, color:C.silver, marginTop:4 }}>{dateLabel}</div>
          </div>
        </div>

        {/* Step dots */}
        <div style={{ display:'flex', gap:4, marginBottom:22 }}>
          {[0,1].map(i => (
            <div key={i} style={{ flex:1, height:3, borderRadius:2, background:i<=step?_T.accent:_T.bg4, transition:'background 0.3s' }}/>
          ))}
        </div>

        {/* ── Step 0: Sleep ── */}
        {step===0 && (
          <div>
            {/* Hours slider */}
            <div style={{ background:_T.bg3, borderRadius:14, padding:'16px', marginBottom:12, border:`1px solid ${_T.cardBorderTop}` }}>
              <div style={{ fontSize:11, color:C.silver, marginBottom:12 }}>{nl?'Uren slaap':'Hours of sleep'}</div>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <input type="range" min={3} max={12} step={0.5} value={hours}
                  onChange={e => setHours(parseFloat(e.target.value))}
                  style={{ flex:1, accentColor:_T.accent }}
                />
                <div style={{ minWidth:48, textAlign:'center', fontSize:22, fontWeight:700, color:hours>=7?C.teal:hours>=5?_T.accent:C.red }}>
                  {hours%1===0 ? hours : hours.toFixed(1)}h
                </div>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
                <span style={{ fontSize:9, color:C.silver }}>3h</span>
                <span style={{ fontSize:9, color:C.teal }}>Score: {sleepScore}/10</span>
                <span style={{ fontSize:9, color:C.silver }}>12h</span>
              </div>
            </div>

            {/* Quality stars */}
            <div style={{ background:_T.bg3, borderRadius:14, padding:'16px', marginBottom:20, border:`1px solid ${_T.cardBorderTop}` }}>
              <div style={{ fontSize:11, color:C.silver, marginBottom:10 }}>{nl?'Slaapkwaliteit':'Sleep quality'}</div>
              <div style={{ display:'flex', gap:8 }}>
                {[1,2,3,4,5].map(s => (
                  <button key={s} onClick={() => setQuality(s)}
                    style={{ flex:1, background:'none', border:'none', cursor:'pointer', fontSize:26, opacity:s<=quality?1:0.2, padding:0, transition:'opacity 0.15s' }}>
                    ⭐
                  </button>
                ))}
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', marginTop:6 }}>
                <span style={{ fontSize:9, color:C.silver }}>{nl?'Slecht':'Poor'}</span>
                <span style={{ fontSize:9, color:C.silver }}>{nl?'Geweldig':'Great'}</span>
              </div>
            </div>

            <button style={s_PB({ width:'100%', fontSize:15 })} onClick={() => setStep(1)}>
              {nl?'Volgende →':'Next →'}
            </button>
            <button style={s_GB({ width:'100%', fontSize:13, marginTop:8 })} onClick={onClose}>
              {nl?'Annuleren':'Cancel'}
            </button>
          </div>
        )}

        {/* ── Step 1: Mood ── */}
        {step===1 && (
          <div>
            <div style={{ display:'flex', gap:6, justifyContent:'space-between', marginBottom:14 }}>
              {MOOD_OPTIONS.map(m => (
                <button key={m.id} onClick={() => setSelMood(m.id)} style={{
                  flex:1, background:selMood===m.id?`${m.color}25`:_T.bg3,
                  border:`2px solid ${selMood===m.id?m.color:_T.cardBorderBot}`,
                  borderRadius:14, padding:'10px 2px', cursor:'pointer',
                  display:'flex', flexDirection:'column', alignItems:'center', gap:4, transition:'all 0.15s',
                }}>
                  <span style={{ fontSize:24 }}>{m.emoji}</span>
                  <span style={{ fontSize:8, color:selMood===m.id?m.color:C.silver }}>{nl?m.label:m.labelEN}</span>
                </button>
              ))}
            </div>

            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
              {QUICK_TAGS.map(tag => (
                <button key={tag} onClick={() => setTags(p => p.includes(tag)?p.filter(t=>t!==tag):[...p,tag])}
                  style={{
                    fontSize:11, padding:'5px 10px', borderRadius:20, cursor:'pointer',
                    background:tags.includes(tag)?`${_T.accent}20`:_T.bg3,
                    border:`1px solid ${tags.includes(tag)?_T.accent:_T.cardBorderBot}`,
                    color:tags.includes(tag)?_T.accent:C.silver,
                  }}>
                  {tag}
                </button>
              ))}
            </div>

            <textarea value={note} onChange={e => setNote(e.target.value)}
              placeholder={nl?'Korte notitie (optioneel)...':'Short note (optional)...'}
              style={{ width:'100%', minHeight:52, background:_T.bg3, border:`1px solid ${_T.cardBorderTop}`, borderRadius:12, color:C.white, fontSize:13, padding:'10px 12px', resize:'none', outline:'none', boxSizing:'border-box', marginBottom:14 }}
            />

            <button style={s_PB({ width:'100%', fontSize:15, opacity:selMood?1:0.4 })} disabled={!selMood} onClick={save}>
              {nl?'Opslaan ✓':'Save ✓'}
            </button>
            <button style={s_GB({ width:'100%', fontSize:13, marginTop:8 })} onClick={save}>
              {nl?'Sla stemming over':'Skip mood & save sleep'}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
